import { UserError } from "@/lib/user-error";
import { formatAmount, toBaseMinor } from "@/lib/currency";

const groupFormatter = new Intl.NumberFormat("en-US");

/** Adds thousands separators to the integer part of a decimal string produced by formatAmount, leaving the fraction (if any) untouched. */
function groupDecimalString(decimal: string): string {
  const [intPart, fracPart] = decimal.split(".");
  const grouped = groupFormatter.format(Number(intPart));
  return fracPart ? `${grouped}.${fracPart}` : grouped;
}

/**
 * Formats an integer minor-unit amount as "₩1,234,567" for KRW, or
 * "USD 1,234.5" for any other currency code — the decimal shape (and
 * trailing-zero trim) comes entirely from currency.ts's formatAmount; this
 * only adds thousands separators and the currency prefix/symbol on top.
 */
export function formatMoney(amountMinor: number, currency: string): string {
  const code = currency.toUpperCase();
  const decimal = formatAmount(amountMinor, code);
  const negative = decimal.startsWith("-");
  const grouped = groupDecimalString(negative ? decimal.slice(1) : decimal);
  const sign = negative ? "-" : "";
  if (code === "KRW") {
    return `${sign}₩${grouped}`;
  }
  return `${sign}${code} ${grouped}`;
}

/** Throws unless x is a non-negative integer (money amounts are always whole minor units). */
export function assertValidAmount(x: number): void {
  if (!Number.isInteger(x) || x < 0) throw new UserError("금액이 올바르지 않습니다");
}

/**
 * Formats a price, appending its base-currency conversion when useful:
 * `"GEL 180 · 약 ₩104,400"`. Falls back to plain `formatMoney` when the
 * amount is already in the trip's base currency, or when no (valid) fx rate
 * is known — lodging prices may be entered without one. The conversion goes
 * through currency.ts's toBaseMinor, so it's exponent-aware (e.g. a 2-decimal
 * currency converting into a 0-decimal base currency) rather than the naive
 * whole-unit `amount * fx` this replaced.
 */
export function formatMoneyWithFx(
  amountMinor: number,
  currency: string,
  fxRateToBase: string | null | undefined,
  baseCurrency: string
): string {
  const rate = fxRateToBase == null ? NaN : Number(fxRateToBase);
  if (currency.toUpperCase() === baseCurrency.toUpperCase() || !Number.isFinite(rate) || rate <= 0) {
    return formatMoney(amountMinor, currency);
  }
  const baseMinor = toBaseMinor(amountMinor, currency, rate, baseCurrency);
  return `${formatMoney(amountMinor, currency)} · 약 ${formatMoney(baseMinor, baseCurrency)}`;
}

/** Currency codes offered by preset in currency selects, before falling back to free text entry. */
export const CURRENCIES = ["KRW", "USD", "EUR", "JPY", "GEL", "TRY"] as const;

/**
 * "낸 돈"/"쓴 돈" 카드 한 줄 표시: 통화별 명목("KRW 100,000 · USD 60") + 기준통화
 * 합계("(합계 ₩178,000)"). 통화별 명목이 없거나(0건), 기준통화 단일 통화뿐이면
 * 명목과 합계가 같은 값이므로 괄호 병기 없이 formatMoney만 반환한다 — 대다수
 * 단일통화 여행에서 "₩100,000 (합계 ₩100,000)" 같은 중복을 피한다.
 */
export function formatMoneyBreakdown(
  byCurrency: { currency: string; amount: number }[],
  totalBase: number,
  baseCurrency: string
): string {
  if (byCurrency.length === 0) return formatMoney(totalBase, baseCurrency);
  if (byCurrency.length === 1 && byCurrency[0].currency === baseCurrency.toUpperCase()) {
    return formatMoney(totalBase, baseCurrency);
  }
  const nominal = byCurrency.map((c) => formatMoney(c.amount, c.currency)).join(" · ");
  return `${nominal} (합계 ${formatMoney(totalBase, baseCurrency)})`;
}
